import React, {useEffect, useState} from 'react';
import MainLayout from '../../mainLayout';
import GenericButton from '../../../components/button';
import styled, {useTheme} from 'styled-components/native';
import {tr} from '../../../translations';
import TextInput from '../../../components/textInput';
import {Theme} from '../../../theme/lightTheme';
import {Keyboard} from 'react-native';
import Animated, {FadeInDown} from 'react-native-reanimated';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {register, updateConfig} from '../login/actions';
import DatePicker from '../../../components/datePicker';
import Picker from '../../../components/picker';
import {connect, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {RegisterProps} from '../../../interfaces/screens/register';
import {checkIfEmailValid, handleChange} from '../../../utils/form';
import {convertToYYYYMMDD} from '../../../utils/helpers';
import {Space} from '../../content/home';
import {SpecialityType} from '../../../interfaces/api/auth/login';
import {getSpecialities} from '../../content/home/actions';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import Icon from '../../../components/icon';
import TextGeneric from '../../../components/textGeneric';
import screens from '../../../config/screens';
import {openUploadModal} from '../../../connected-components/uploadModal/actions';
import ProgressiveImage from '../../../components/progressiveImage';
import {TouchableOpacity} from 'react-native';

const ContentContainer = styled.View<{wdp: any; hdp: any}>`
  padding: ${({hdp}) => hdp(5)}px ${({wdp}) => wdp(5)}px;
`;

const LogoContainer = styled(Animated.View)`
  align-items: center;
  justify-content: center;
  align-self: center;
`;

const Slogan = styled(TextGeneric)<{paddingBottom: number}>`
  text-align: center;
  padding-bottom: ${({paddingBottom}) => paddingBottom}px;
`;

const UploadImageContainer = styled(TouchableOpacity)<{wdp: any}>`
  width: ${({wdp}) => wdp(30)}px;
  margin-vertical: 5%;
  align-self: center;
`;

const ImageContainer = styled.View`
  height: ${({theme}) => theme.text.s1 * 2.5}px;
  aspect-ratio: 1;
  border-radius: ${({theme}) => theme.text.s1 * 2.5}px;
  align-self: center;
  background-color: ${({theme}) => theme.border};
  border-color: ${({theme}) => theme.primary + 'CC'};
  border-width: 4px;
`;

const ProfileImage = styled(ProgressiveImage)<{wdp: any}>`
  height: 100%;
  width: 100%;
  border-radius: ${({wdp}) => wdp(15)}px;
`;

const EditIconContainer = styled.View`
  background-color: ${({theme}) => theme.primary};
  padding: 5%;
  align-items: center;
  justify-content: center;
  display: flex;
  position: absolute;
  right: -${({theme}) => theme.text.s55 / 2}px;
  top: -${({theme}) => theme.text.s55 / 3}px;
  border-radius: 12px;
  border-width: 2px;
  border-color: ${({theme}) => theme.primary};
  opacity: 0.9;
`;

const SubmitContainer = styled.View`
  margin-top: 20px;
  align-self: center;
`;

const Row = styled.View`
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  align-items: flex-start;
`;

const FormItemContainer = styled.View<{isLargeScreen: boolean}>`
  width: ${({isLargeScreen}) => (isLargeScreen ? '48%' : '100%')};
`;

const LinkText = styled.TouchableOpacity`
  color: ${({theme}) => theme.text.dark};
  font-weight: bold;
  font-size: ${({theme}) => theme.text.s8}px;
  text-align: center;
  padding-top: 5%;
  padding-bottom: 5%;
  text-decoration: underline;
  cursor: pointer;
`;


// Define the mandatory fields for the form
const mandatoryFields = [
  'email',
  'password',
  'confirmPassword',
  'firstName',
  'lastName',
  'bod',
  'gender',
  'country',
  'specialty',
];
const ANIMATION_DURATION = 400;
/**
 * Register screen component.
 *
 * @param {RegisterProps} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const Register: React.FC<RegisterProps> = ({navigation}) => {
  const theme = useTheme() as Theme;
  const {isLargeScreen, hdp, wdp, getByScreenSize} = useResponsiveDimensions();
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {config} = useSelector((state: RootState) => state.auth) || {
    currentUser: {},
  };
  const [errors, setErrors] = useState<any>({});
  const [image, setImage] = useState<{
    uri?: string;
    name: string;
    type: string;
  }>();

  const [localSpecialties, setLocalSpecialties] = useState<SpecialityType[]>(
    config?.specialties || [],
  );

  const [form, updateForm] = useState<any>({
    profession: {id: 1, name: 'Physician'},
  });

  const uploadImage = () =>
    openUploadModal({
      onSelectionChange: (file: any) => {
        if (file) {
          setImage(file);
        }
      },
      options: {
        mediaType: 'photo',
      },
    });

  useEffect(() => {
    updateConfig();
  }, []);

  const validate = (): boolean => {
    const newErrors: any = {};
    const requiredFields = [
      'email',
      'password',
      'confirmPassword',
      'firstName',
      'lastName',
    ];
    requiredFields.forEach(field => {
      if (!form[field]) {
        newErrors[field] = tr('validation.required', 'This field is required');
      }
    });

    if (form.email && !checkIfEmailValid(form.email)) {
      newErrors.email = tr(
        'validation.invalidEmail',
        'Please enter a valid email',
      );
    }

    if (form.password && form.password !== form.confirmPassword) {
      newErrors.confirmPassword = tr(
        'validation.passwordsDoNotMatch',
        'Passwords do not match',
      );
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateSpecialities = async () => {
    if (form?.profession?.id) {
      const res = await getSpecialities(1);
      setLocalSpecialties(res);
      handleChange('specialty', res?.[0] || null, updateForm);
    }
  };

  useEffect(() => {
    updateSpecialities();
  }, [form.profession]);

  const _getPickerList = (label: string): any[] => {
    switch (label) {
      case 'gender':
        return config?.genders || [];
      case 'language':
        return config?.languages || [];
      case 'profession':
        return config?.professions || [];
      case 'specialty':
        return localSpecialties || [];
      case 'country':
        return config?.countries || [];
      default:
        return [];
    }
  };

  const getObjectByName = (label: string, name: string | number): any => {
    const data = _getPickerList(label);
    const idx = data.findIndex(i => i.name === name);
    if (idx > -1) {
      return data[idx];
    }
    return {};
  };

  // Function to handle form submission
  const submit = async () => {
    Keyboard.dismiss();

    if (!validate()) {
      return;
    }

    const {
      email,
      password,
      confirmPassword,
      firstName,
      lastName,
      bod,
      gender,
      country,
      profession,
      specialty,
      language,
    } = form;

    const body: any = {
      email: email,
      password: password,
      confirm_password: confirmPassword,
      first_name: firstName,
      last_name: lastName,
      date_of_birth: bod,
      gender_id: gender?.id,
      country_id: country?.id,
      profession_id: profession?.id,
      specialty_id: specialty?.id,
      language_id: language?.id || 2,
      is_first_login: 0,
      image,
    };

    await register(body);
  };

  const loginPressHandler = () => navigation?.navigate(screens.auth.login);

  const renderFormItem = (item: string) => {
    if (item === 'bod') {
      return (
        <DatePicker
          key={item}
          label={tr(`register.${item}`)}
          value={convertToYYYYMMDD(form[item]) ?? ''}
          maxDate={new Date().toISOString()}
          style={{
            marginBottom: hdp(2),
          }}
          onValueChange={text => handleChange(item, text, updateForm)}
          format={'YYYY-MM-DD'}
        />
      );
    }
    if (
      item === 'gender' ||
      item === 'language' ||
      item === 'specialty' ||
      item === 'country'
    ) {
      return (
        <Picker
          key={item}
          label={tr(`register.${item}`)}
          containerStyle={{
            marginBottom: hdp(2),
          }}
          list={_getPickerList(item).map(i => i?.name || '')}
          value={form[item] ? form[item].name : ''}
          onValueChange={val => {
            const value = getObjectByName(item, val);
            handleChange(item, value, updateForm);
          }}
        />
      );
    }
    
    return (
      <React.Fragment key={item}>
        <TextInput
          label={tr(`register.${item}`)}
          secureInput={item === 'password' || item === 'confirmPassword'}
          value={form[item]}
          onChangeText={text => handleChange(item, text, updateForm)}
          containerStyle={{
            marginBottom: hdp(2),
          }}
          errorMessage={errors[item]}
          hasError={!!errors[item]}
          enableErrorMessage
        />
      </React.Fragment>
    );
  };

  return (
    <MainLayout
      isCustomHeader
      showOverlay={false}
      noPadding
      backHeader
      title={tr('register.title')}
      keyboardAvoidScrollView
      noPaddingHeader>
      <ContentContainer wdp={wdp} hdp={hdp}>
        <LogoContainer entering={FadeInDown.duration(ANIMATION_DURATION)}>
          <Icon
            name={'genio'}
            width={wdp(27.3)}
            height={hdp(7.3)}
            color={theme.secondary}
          />
          <Slogan bold color={theme.text.dark} paddingBottom={hdp(5)}>
            {tr('app.slogan')}
          </Slogan>
        </LogoContainer>
        {/*<UploadImageContainer onPress={uploadImage} wdp={wdp}>*/}
        {/*  <ImageContainer theme={theme}>*/}
        {/*    <ProfileImage*/}
        {/*      defaultImage={require('../../../assets/images/user.png')}*/}
        {/*      wdp={wdp}*/}
        {/*      source={{*/}
        {/*        uri: image?.uri,*/}
        {/*      }}*/}
        {/*    />*/}
        {/*    <EditIconContainer theme={theme}>*/}
        {/*      <Icon*/}
        {/*        name={'edit'}*/}
        {/*        color={theme.homeBackground}*/}
        {/*        size={theme.text.s55}*/}
        {/*      />*/}
        {/*    </EditIconContainer>*/}
        {/*  </ImageContainer>*/}
        {/*</UploadImageContainer>*/}
        <Animated.View
          entering={FadeInDown.duration(ANIMATION_DURATION).delay(
            ANIMATION_DURATION,
          )}>
          {isLargeScreen
            ? mandatoryFields.reduce((acc, field, index) => {
                if (index % 2 === 0) {
                  const nextField = mandatoryFields[index + 1];
                  acc.push(
                    <Row key={field}>
                      <FormItemContainer isLargeScreen={isLargeScreen}>
                        {renderFormItem(field)}
                      </FormItemContainer>
                      {nextField && (
                        <FormItemContainer isLargeScreen={isLargeScreen}>
                          {renderFormItem(nextField)}
                        </FormItemContainer>
                      )}
                    </Row>,
                  );
                }
                return acc;
              }, [] as React.ReactElement[])
            : mandatoryFields.map(item => (
                <FormItemContainer key={item} isLargeScreen={isLargeScreen}>
                  {renderFormItem(item)}
                </FormItemContainer>
              ))}
          <SubmitContainer style={{width: getByScreenSize('100%', '40%')}}>
            <GenericButton onPress={submit} label={tr('register.signUp')} />
          </SubmitContainer>
          <TextGeneric
            style={{
              color: theme.text.dark,
              fontSize: theme.text.s8,
              marginTop: theme.text.s8,
              textAlign: 'center',
            }}>
            {tr('register.loginPart1')}{' '}
            <LinkText onPress={loginPressHandler}>
              {tr('register.loginPart2')}
            </LinkText>
          </TextGeneric>
        </Animated.View>
      </ContentContainer>
      <Space bottomInset={bottomInset} extraSpace={hdp(2)} />
    </MainLayout>
  );
};

const mapStateToProps = () => ({});
export default connect(mapStateToProps, null)(Register);
